import React from "react";
import PropTypes from "prop-types";
import { Box, Flex, Icon } from "orcs-design-system";
import styled, { keyframes } from "styled-components";

import { GradientBorder, Content } from "../StyledComponents";
import { AIMessageWrapper } from "./AIMessageWrapper";

const PIPELINE_STEPS = [
  { key: "gather_context", label: "Gathering context" },
  { key: "classifier", label: "Classifying request" },
  { key: "planning", label: "Planning dashboard" },
  { key: "transform_data", label: "Creating transforms" },
  { key: "build_charts", label: "Configuring charts" },
  { key: "build_template", label: "Generating layout" },
  { key: "persist_dashboard", label: "Saving dashboard" },
];

const stepPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const PulseDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #3b82f6;
  animation: ${stepPulse} 1.5s ease-in-out infinite;
`;

const StepIndicator = ({ status }) => {
  if (status === "completed") {
    return <Icon icon={["fas", "check-circle"]} color="success" size="xs" />;
  }
  if (status === "error") {
    return (
      <Icon icon={["fas", "exclamation-circle"]} color="danger" size="xs" />
    );
  }
  if (status === "skipped") {
    return <Icon icon={["fas", "forward"]} color="greyDark" size="xs" />;
  }
  if (status === "started" || status === "retrying") {
    return <PulseDot />;
  }
  return (
    <Box
      width="8px"
      height="8px"
      borderRadius="50%"
      bg="greyLight"
      flexShrink={0}
    />
  );
};

StepIndicator.propTypes = {
  status: PropTypes.string,
};

/**
 * Live dashboard build progress checklist. Driven by `pipelineSteps` from the
 * unified chat provider, so it renders in the consolidated pane for both
 * sidebar and dashboard-page contexts.
 */
export const PipelineProgress = ({ pipelineSteps }) => {
  if (!pipelineSteps || Object.keys(pipelineSteps).length === 0) {
    return null;
  }

  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      mt="4px"
      mb="4px"
      gap="8px"
    >
      <AIMessageWrapper>
        <GradientBorder>
          <Content aria-label="Building dashboard">
            <Flex flexDirection="column" gap="6px" padding="4px 0">
              {PIPELINE_STEPS.map((step) => {
                const status = pipelineSteps[step.key];
                if (!status) {
                  return null;
                }
                const isDone = status === "completed";
                const isSkipped = status === "skipped";
                const isError = status === "error";
                const isActive = status === "started" || status === "retrying";
                return (
                  <Flex key={step.key} alignItems="center" gap="8px">
                    <StepIndicator status={status} />
                    <Box
                      fontSize="13px"
                      color={
                        isError
                          ? "danger"
                          : isDone || isSkipped
                            ? "greyDark"
                            : isActive
                              ? "greyDarkest"
                              : "greyLight"
                      }
                      fontWeight={isActive ? 600 : 400}
                    >
                      {step.label}
                      {status === "retrying" && " (retrying...)"}
                    </Box>
                  </Flex>
                );
              })}
            </Flex>
          </Content>
        </GradientBorder>
      </AIMessageWrapper>
    </Flex>
  );
};

PipelineProgress.propTypes = {
  pipelineSteps: PropTypes.object,
};

export default PipelineProgress;
